import React, { useCallback, useEffect, useState } from 'react';
import { fetchRegulatoryNewsUsage, RegulatoryNewsUsageRecord, toDate } from '../../api/regulatoryNews';

export const RegulatoryNewsUsageTab: React.FC = () => {
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(tomorrow);
  const [records, setRecords] = useState<RegulatoryNewsUsageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchRegulatoryNewsUsage({ dateFrom, dateTo });
      setRecords(data);
    } catch (e: any) {
      setError(e.response?.data?.error || 'Could not load usage records.');
    }
    setLoading(false);
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const totalCost = records.reduce((s, r) => s + (r.costUsd || 0), 0);
  const totalTokens = records.reduce((s, r) => s + (r.totalTokens || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, marginTop: 4, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>From</label>
          <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="form-label" style={{ fontSize: 12 }}>To</label>
          <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <button className="btn btn-sm btn-outline" onClick={load}>Refresh</button>
      </div>

      {loading && <p className="text-muted text-sm">Loading…</p>}
      {!loading && error && (
        <p style={{ color: 'var(--c-error)', fontSize: 13, marginBottom: 8 }}>⚠ {error}</p>
      )}
      {!loading && !error && (
        <>
          <p className="text-sm" style={{ marginBottom: 8 }}>
            <strong>{records.length}</strong> Gemini calls · Total tokens: <strong>{totalTokens.toLocaleString()}</strong> · Total cost: <strong>${totalCost.toFixed(4)}</strong>
          </p>
          {records.length === 0 ? (
            <p className="text-muted text-sm">No usage records found for this period.</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date &amp; Time</th>
                    <th>Regulator</th>
                    <th>Article</th>
                    <th>Model</th>
                    <th>Input</th>
                    <th>Output (incl. thinking)</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {records.slice(0, 200).map((r) => {
                    const ts = toDate(r.timestamp);
                    const dateTime = `${ts.toLocaleDateString()} ${ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                    return (
                      <tr key={r.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{dateTime}</td>
                        <td>{r.regulator}</td>
                        <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.articleTitle}>
                          {r.articleTitle}
                        </td>
                        <td>{r.generatedBy === 'fallback' ? 'fallback (no call)' : r.model}</td>
                        <td>{(r.promptTokens || 0).toLocaleString()}</td>
                        <td>{(r.outputTokens || 0).toLocaleString()}</td>
                        <td>${(r.costUsd || 0).toFixed(4)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
